import { Paper } from '@mui/material';
import { type Serie, type SliceTooltipProps } from '@nivo/line';
import type {
  DraftPickYearResponse,
  DraftPickYearResponsePagedData,
} from '@on3/api';
import { Text } from 'components/UI/Typography/Text';
import dynamic from 'next/dynamic';
import { type FC, useMemo } from 'react';

import styles from './PicksByYear.module.scss';

const ResponsiveLine = dynamic(
  () => import('@nivo/line').then((m) => m.ResponsiveLine),
  { ssr: false },
);

interface IPicksByYearProps {
  yearPicks: DraftPickYearResponsePagedData;
  picksTitle?: string;
}

const formattedData = (
  list: DraftPickYearResponse[],
): { max: number; maxX: number; data: Serie['data'] } =>
  list.reduce(
    (acc, curr) => {
      if (acc.data.some((d) => d.x === curr.year)) {
        return { ...acc };
      }

      return {
        ...acc,
        data: [...acc.data, { x: curr.year, y: curr.count }],
        max: curr.count && curr.count > acc.max ? curr.count : acc.max,
        maxX: curr.year && curr.year > acc.maxX ? curr.year : acc.maxX,
      };
    },
    { max: 0, maxX: 2000, data: [] as Serie['data'] },
  );

const graphMargin = { top: 20, right: 20, bottom: 30, left: 25 };

const SliceTooltip: () => FC<SliceTooltipProps> = () =>
  // eslint-disable-next-line react/display-name
  function ({ slice }: SliceTooltipProps) {
    return <CustomTooltip axis={'x'} slice={slice} />;
  };

export const PicksByYear = ({ yearPicks, picksTitle }: IPicksByYearProps) => {
  const { list: yearPicksList } = yearPicks;

  const { gridYValues, gridXValues, chartData } = useMemo(() => {
    const {
      max: maxYVal,
      maxX: maxXVal,
      data,
    } = formattedData(yearPicksList || []);

    return {
      gridYValues: [
        0,
        maxYVal - 10,
        maxYVal - 8,
        maxYVal - 6,
        maxYVal - 4,
        maxYVal - 2,
        maxYVal,
      ],
      gridXValues: [
        2000,
        maxXVal - 16,
        maxXVal - 12,
        maxXVal - 8,
        maxXVal - 4,
        maxXVal,
      ],
      chartData: [
        {
          id: 'Draft By Year Data',
          color: '#FF0013',
          data,
        },
      ],
    };
  }, [yearPicksList]);

  const axisBottom = useMemo(
    () => ({ tickValues: gridXValues }),
    [gridXValues],
  );

  const axisLeft = useMemo(() => ({ tickValues: gridYValues }), [gridYValues]);

  return (
    <section className={styles.pickYearContainer}>
      <Text className={styles.yearTitle} component="h2" variant="h6">
        {picksTitle}
      </Text>
      <ResponsiveLine
        animate={true}
        axisBottom={axisBottom}
        axisLeft={axisLeft}
        colors="#FE3A1E"
        curve="monotoneX"
        data={chartData}
        enableGridX={false}
        enableGridY={false}
        enablePoints={false}
        enableSlices="x"
        margin={graphMargin}
        sliceTooltip={SliceTooltip()}
        useMesh={true}
      />
    </section>
  );
};

const CustomTooltip = ({ slice }: SliceTooltipProps) => {
  return (
    <Paper className={styles.tooltipContainer}>
      <Text variant="h3">
        Count:
        <span className={styles.slicePoint}>
          {slice.points?.[0].data.yFormatted}
        </span>
      </Text>
      <Text variant="h3">
        Year:
        <span className={styles.slicePoint}>
          {slice.points?.[0].data.xFormatted}
        </span>
      </Text>
    </Paper>
  );
};
